<template>
  <a-modal
    :visible="visible"
    :title="isEdit ? '상품 수정' : '상품 추가'"
    @cancel="handleCancel"
    :confirm-loading="loading"
    :footer="null"
    @keyup.enter="handleSave"
  >
    <a-form ref="formRef" :model="form" layout="vertical" class="modal-form" :rules="rules">
      <a-form-item label="상품 그룹" name="productGroup" required>
        <a-input v-model:value="form.productGroup" placeholder="상품 그룹을 입력하세요" />
      </a-form-item>

      <a-form-item label="상품명" name="productName" required>
        <a-input v-model:value="form.productName" placeholder="상품명을 입력하세요" />
      </a-form-item>

      <a-form-item label="과금 방식" name="billingType" required>
        <a-radio-group v-model:value="form.billingType">
          <a-radio
            v-for="bt in billingTypeOptions"
            :key="bt.value"
            :value="bt.value"
            :disabled="bt.disabled"
          >
            {{ bt.label }}
          </a-radio>
        </a-radio-group>
      </a-form-item>

      <a-form-item label="설치비(VAT포함)" name="installationFee" required>
        <a-input-number
          v-model:value="form.installationFee"
          :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
          :parser="value => value.replace(/\$\s?|(,*)/g, '')"
          style="width: 100%"
          placeholder="설치비를 입력하세요"
          :min="0"
          :step="1000"
        />
      </a-form-item>

      <a-form-item :label="usageFeeLabel" name="price" required>
        <a-input-number
          v-model:value="form.price"
          :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
          :parser="value => value.replace(/\$\s?|(,*)/g, '')"
          style="width: 100%"
          placeholder="사용료를 입력하세요"
          :min="0"
          :step="1000"
        />
      </a-form-item>

      <a-form-item label="설명" name="description">
        <a-textarea
          v-model:value="form.description"
          placeholder="상품 설명을 입력하세요"
          :rows="4"
        />
      </a-form-item>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :loading="loading"
          @click="handleSave"
          class="yellow-button"
        >저장</a-button>
      </div>
    </a-form>
  </a-modal>
</template>

<script>
import { BillingTypes, getUsageFeeLabel } from '../enums';

export default {
  name: "UpsertProductModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    productData: {
      type: Object,
      default: () => ({
        key: '',
        productGroup: '센터플로우',
        productName: '',
        billingType: 'monthly',
        installationFee: 0,
        price: 0,
        description: ''
      })
    }
  },
  data() {
    return {
      form: {
        key: '',
        productGroup: '센터플로우',
        productName: '',
        billingType: 'monthly',
        installationFee: 0,
        price: 0,
        description: ''
      },
      billingTypeOptions: Object.values(BillingTypes),
      rules: {
        productGroup: [
          { required: true, message: '상품 그룹을 입력해주세요', trigger: 'blur' }
        ],
        productName: [
          { required: true, message: '상품명을 입력해주세요', trigger: 'blur' }
        ],
        billingType: [
          { required: true, message: '과금 방식을 선택해주세요', trigger: 'change' }
        ],
        installationFee: [
          { required: true, message: '설치비를 입력해주세요', trigger: 'blur' },
          { type: 'number', min: 0, message: '설치비는 0 이상이어야 합니다', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '사용료를 입력해주세요', trigger: 'blur' },
          { type: 'number', min: 0, message: '사용료는 0 이상이어야 합니다', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    usageFeeLabel() {
      return getUsageFeeLabel(this.form.billingType);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        if (this.isEdit) {
          this.form = { ...this.productData };
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        key: '',
        productGroup: '센터플로우',
        productName: '',
        billingType: 'monthly',
        installationFee: 0,
        price: 0,
        description: ''
      };
    },
    handleCancel() {
      this.$emit('cancel');
    },
    handleSave() {
      const formRef = this.$refs.formRef;
      if (formRef) {
        formRef.validate().then(valid => {
          if (valid) {
            let submitData = {
              ...this.form,
              installationFee: Number(this.form.installationFee || 0),
              price: Number(this.form.price || 0)
            };

            if (!this.isEdit) {
              const { key, ...dataWithoutKey } = submitData;
              submitData = dataWithoutKey;
            }

            this.$emit('save', submitData);
          } else {
            this.$message.error('입력 정보를 확인해주세요.');
          }
        }).catch(error => {
          console.error('폼 유효성 검사 중 오류 발생:', error);
          this.$message.error('입력 정보를 확인해주세요.');
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

.modal-form {
  padding: 20px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;

  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
